import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BsX } from 'react-icons/bs';
import './DeckSimulator.css';

const COIN = { uid: 'coin', cardid: 'GAME_005', cardname: 'The Coin', cost: 0, type: 1, isCoin: true };
const costOf = (card) => Math.max(0, Math.floor(Number(card.cost) || 0));
const imageOf = (card) => Number(card.type) === 1
  ? `https://art.hearthstonejson.com/v1/render/latest/enUS/256x/${encodeURIComponent(card.cardid)}.png`
  : `https://www.hearthcards.net/cards/${encodeURIComponent(card.cardid)}.webp`;

function shuffle(cards) {
  const result = [...cards];
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

function newGame(cards, second = true) {
  const pile = shuffle(cards.flatMap((card, index) => Array.from(
    { length: Math.max(1, Math.min(100, Math.floor(Number(card.quantity) || 1))) },
    (_, copy) => ({ ...card, uid: `${index}-${copy}` }),
  )));
  const openingSize = second ? 4 : 3;
  return { phase: 'mulligan', second, hand: pile.slice(0, openingSize), pile: pile.slice(openingSize), replace: [],
    played: [], turn: 0, mana: 0, maxMana: 0, notice: '' };
}

function draw(state) {
  if (!state.pile.length) return { ...state, notice: 'No cards left in the deck.' };
  const [card, ...pile] = state.pile;
  if (state.hand.length >= 10) return { ...state, pile, notice: `Hand full — ${card.cardname || 'a card'} was burned.` };
  return { ...state, pile, hand: [...state.hand, card], notice: '' };
}

export default function DeckSimulator({ cards, title, onClose }) {
  const [game, setGame] = useState(() => newGame(cards));
  const dialogRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const mulligan = game.phase === 'mulligan';

  useEffect(() => {
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.focus();
    const onKey = (event) => {
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); closeRef.current(); }
      if (event.key !== 'Tab') return;
      const buttons = Array.from(dialogRef.current?.querySelectorAll('button:not(:disabled)') || []);
      const first = buttons[0];
      const last = buttons[buttons.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialogRef.current)) { event.preventDefault(); first?.focus(); }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKey, true);
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, []);

  const start = () => setGame((current) => {
    if (current.phase !== 'mulligan') return current;
    // Draw replacement copies before returning the rejected copies to the deck.
    const removed = current.hand.filter((card) => current.replace.includes(card.uid)).slice(0, current.pile.length);
    const replacements = current.pile.slice(0, removed.length);
    let replacementIndex = 0;
    const hand = current.hand.map((card) => removed.some((item) => item.uid === card.uid) ? replacements[replacementIndex++] : card);
    if (current.second) hand.push({ ...COIN });
    return draw({ ...current, phase: 'playing', hand,
      pile: shuffle([...current.pile.slice(removed.length), ...removed]), replace: [], turn: 1, maxMana: 1, mana: 1 });
  });

  const clickCard = (uid) => setGame((current) => {
    if (current.phase === 'mulligan') {
      return { ...current, replace: current.replace.includes(uid) ? current.replace.filter((id) => id !== uid) : [...current.replace, uid] };
    }
    const card = current.hand.find((item) => item.uid === uid);
    if (!card) return current;
    if (costOf(card) > current.mana) return { ...current, notice: 'Not enough mana. End your turn to refill.' };
    return { ...current, hand: current.hand.filter((item) => item.uid !== uid),
      played: card.isCoin ? current.played : [...current.played, card],
      mana: card.isCoin ? Math.min(10, current.mana + 1) : current.mana - costOf(card), notice: '' };
  });

  const removeCard = (uid) => setGame((current) => ({ ...current, played: current.played.filter((card) => card.uid !== uid) }));

  const endTurn = () => setGame((current) => {
    if (current.phase !== 'playing') return current;
    const maxMana = Math.min(10, current.maxMana + 1);
    return draw({ ...current, turn: current.turn + 1, maxMana, mana: maxMana });
  });

  return createPortal(<div className="deck-sim-overlay" onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <section className={`deck-sim-dialog ${mulligan ? 'is-mulligan' : ''}`} role="dialog" aria-modal="true" aria-label={`${title} deck simulator`} tabIndex={-1} ref={dialogRef}>
      <header className="deck-sim-header"><h2>{title}</h2><button type="button" className="deck-sim-close" onClick={onClose} aria-label="Close simulator"><BsX /></button></header>
      {mulligan ? <>
        <div className="deck-sim-opening-title"><h3>Choose your opening hand</h3><button type="button" className="deck-sim-order-toggle" aria-label="Toggle going first or second; changes the opening hand" aria-pressed={game.second} onClick={() => setGame(newGame(cards, !game.second))}>{game.second ? 'Going second · 4 cards + Coin' : 'Going first · 3 cards'}</button><p>Click a card to mark it with an X and replace it.</p></div>
        <div className="deck-sim-opening deck-sim-card-row">{game.hand.map((card) => {
          const marked = game.replace.includes(card.uid);
          return <button key={card.uid} type="button" className={`deck-sim-card ${marked ? 'is-marked' : ''}`} onClick={() => clickCard(card.uid)} aria-pressed={marked} aria-label={`${card.cardname || 'Card'}: ${marked ? 'marked for replacement' : 'keep'}`}>
            <img src={imageOf(card)} alt={card.cardname || 'Card'} draggable="false" />
            {marked && <span className="deck-sim-replace-mark" aria-hidden="true"><BsX /></span>}
          </button>;
        })}</div>
        <div className="deck-sim-start"><button type="button" className="deck-sim-primary" onClick={start}>{game.replace.length ? 'Mulligan & continue' : 'Continue'}</button></div>
      </> : <>
        <div className="deck-sim-turn"><span>Turn {game.turn}</span><div className="deck-sim-mana" aria-label={`${game.mana} of ${game.maxMana} mana available`}>
          {Array.from({ length: Math.max(game.maxMana, game.mana) }, (_, index) => <span key={index} className={index < game.mana ? 'is-filled' : ''} />)}<strong>{game.mana}/{game.maxMana} mana</strong>
        </div><button type="button" className="deck-sim-primary" onClick={endTurn}>End turn</button></div>
        <div className="deck-sim-table">{game.played.length ? <><p>Played cards · click to remove</p><div className="deck-sim-card-row">{game.played.map((card) => <button type="button" className="deck-sim-card" key={card.uid} onClick={() => removeCard(card.uid)} aria-label={`Remove ${card.cardname || 'card'} from the board`}><img src={imageOf(card)} alt={card.cardname || 'Played card'} draggable="false" /></button>)}</div></> : <p className="deck-sim-empty">Click a card in your hand to play it.</p>}</div>
        <div className="deck-sim-hand"><div className="deck-sim-hand-label"><span>Your hand</span><span>{game.pile.length} cards left in deck</span></div><div className="deck-sim-card-row">{game.hand.map((card) => <button type="button" key={card.uid} className={`deck-sim-card ${costOf(card) <= game.mana ? 'is-playable' : ''}`} onClick={() => clickCard(card.uid)} aria-disabled={costOf(card) > game.mana} aria-label={`Play ${card.cardname || 'card'}, ${costOf(card)} mana`}>
          <img src={imageOf(card)} alt={card.cardname || 'Card'} draggable="false" />
        </button>)}{!game.hand.length && <p className="deck-sim-empty">End your turn to draw a card.</p>}</div></div>
        <div className="deck-sim-notice" role="status" aria-live="polite">{game.notice}</div>
      </>}
    </section>
  </div>, document.body);
}